import type { ModalProps as AntdModalProps } from 'antd';
import { Modal as AntdModal, Button } from 'antd';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import useMediaQuery from 'use-media-antd-query';

import './modal.scss';

type ModalPropsType = Readonly<{
  isModalOpen: boolean;
  headerTitle: ReactNode;
  width?: AntdModalProps['width'];
  height?: number | string;
  submitText?: string;
  cancelText?: string;
  withNoFooter?: boolean;
  withScrollBars?: boolean;
  changeStateOfModal: (status: boolean) => void;
  onSubmit?: () => void;
  onCancel?: () => void;
  children: ReactNode;
  isFullScreen?: boolean;
  fullScreenInset?: number;
  closeIcon?: AntdModalProps['closeIcon'];
  submitButtonClassName?: string;
  centered?: boolean;
  isNoCancelButton?: boolean;
  isNoSubmitButton?: boolean;
  wrapClassName?: string;
  isDisabledSubmitButton?: boolean;
  mask?: boolean;
  zIndex?: number;
}>;

const Modal = ({
  isModalOpen,
  isFullScreen = false,
  fullScreenInset,
  closeIcon = true,
  headerTitle,
  submitButtonClassName = 'call-to-action-button--purple',
  centered = true,
  isNoCancelButton = false,
  isNoSubmitButton = false,
  submitText = '',
  cancelText = '',
  wrapClassName,
  withScrollBars = false,
  withNoFooter = false,
  isDisabledSubmitButton = false,
  onSubmit,
  mask = true,
  zIndex = 12000,
  width,
  height,
  onCancel,
  changeStateOfModal,
  children,
}: ModalPropsType) => {
  const colSize = useMediaQuery();
  const isCompactScreen = colSize === 'xs' || colSize === 'sm' || colSize === 'md';
  const resolvedFullScreenInset = fullScreenInset ?? (isCompactScreen ? 12 : 100);
  const fullScreenWidth = `calc(100vw - ${resolvedFullScreenInset * 2}px)`;
  const fullScreenHeight = `calc(100dvh - ${resolvedFullScreenInset * 2}px)`;

  const handleClose = () => {
    changeStateOfModal(false);
    onCancel?.();
  };

  const handleSubmit = () => {
    onSubmit?.();
    changeStateOfModal(false);
  };

  const shouldHideFooter = withNoFooter || (isNoCancelButton && isNoSubmitButton);
  const resolvedSubmitButtonClassName = submitButtonClassName
    .split(/\s+/)
    .filter(Boolean)
    .map((className) => (className === 'action-button--yellow' ? 'call-to-action-button--purple' : className))
    .join(' ');

  return (
    <AntdModal
      centered={isFullScreen ? false : centered}
      width={isFullScreen ? fullScreenWidth : width}
      closeIcon={closeIcon}
      mask={mask}
      zIndex={zIndex}
      destroyOnHidden
      wrapClassName={classNames(wrapClassName, {
        'shared-modal--fullscreen': isFullScreen,
      })}
      title={headerTitle}
      open={isModalOpen}
      style={
        isFullScreen
          ? {
              top: resolvedFullScreenInset,
              width: fullScreenWidth,
              maxWidth: fullScreenWidth,
              paddingBottom: 0,
            }
          : undefined
      }
      styles={
        isFullScreen
          ? {
              content: {
                width: '100%',
                height: fullScreenHeight,
                borderRadius: 16,
                padding: 0,
                overflow: 'hidden',
              },
              header: {
                padding: '16px 24px',
                marginBottom: 0,
              },
              body: {
                height: shouldHideFooter
                  ? headerTitle
                    ? `calc(${fullScreenHeight} - 56px)`
                    : fullScreenHeight
                  : headerTitle
                    ? `calc(${fullScreenHeight} - 56px - 72px)`
                    : `calc(${fullScreenHeight} - 72px)`,
                overflow: 'hidden',
                padding: 0,
              },
              footer: {
                padding: '12px 24px',
                marginTop: 0,
              },
            }
          : height
            ? {
                body: {
                  height,
                },
              }
            : undefined
      }
      footer={
        shouldHideFooter ? null : (
          <>
            {!isNoCancelButton && (
              <Button className="call-to-action-button--white" key="back" size="large" onClick={handleClose}>
                {cancelText}
              </Button>
            )}

            {!isNoSubmitButton && (
              <Button
                key="submit"
                type="primary"
                size="large"
                className={resolvedSubmitButtonClassName}
                disabled={isDisabledSubmitButton}
                onClick={handleSubmit}
              >
                {submitText}
              </Button>
            )}
          </>
        )
      }
      onCancel={handleClose}
    >
      {children}
    </AntdModal>
  );
};

export default Modal;
